<script lang="ts">
  import { page } from "$app/stores"
  import {
    absoluteUrl,
    canonicalUrl,
    defaultOgImage,
    jsonLdScript,
    siteDescription,
    siteName,
    siteTitle,
  } from "$lib/seo"

  /** Page title. Appended with the site name unless it already is the site name. */
  export let title: string = siteTitle
  export let description: string = siteDescription
  /** Override the canonical path. Defaults to the current route. */
  export let canonical: string | undefined = undefined
  export let image: string = defaultOgImage
  export let type: "website" | "article" | "profile" = "website"
  export let noindex: boolean = false
  /** Article timestamps, only emitted when type is "article". */
  export let publishedTime: string | undefined = undefined
  export let modifiedTime: string | undefined = undefined
  /** JSON-LD schema object (without @context) to embed on the page. */
  export let schema: object | undefined = undefined

  $: fullTitle = title === siteTitle ? title : `${title} | ${siteName}`
  $: url = canonical ? absoluteUrl(canonical) : canonicalUrl($page.url.pathname)
  $: imageUrl = absoluteUrl(image)
  $: jsonLd = schema
    ? jsonLdScript({ "@context": "https://schema.org", ...schema })
    : undefined
</script>

<svelte:head>
  <title>{fullTitle}</title>
  <meta name="description" content={description} />
  <link rel="canonical" href={url} />
  <meta
    name="robots"
    content={noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"}
  />

  <!-- Open Graph -->
  <meta property="og:type" content={type} />
  <meta property="og:site_name" content={siteName} />
  <meta property="og:locale" content="en_GB" />
  <meta property="og:url" content={url} />
  <meta property="og:title" content={fullTitle} />
  <meta property="og:description" content={description} />
  <meta property="og:image" content={imageUrl} />
  <meta property="og:image:alt" content={fullTitle} />

  {#if type === "article" && publishedTime}
    <meta property="article:published_time" content={publishedTime} />
  {/if}
  {#if type === "article" && modifiedTime}
    <meta property="article:modified_time" content={modifiedTime} />
  {/if}

  <!-- Twitter / X -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={fullTitle} />
  <meta name="twitter:description" content={description} />
  <meta name="twitter:image" content={imageUrl} />

  {#if jsonLd}
    <!-- JSON-LD has to be injected as a raw <script> tag; Svelte will not render
         one declared literally in markup. This is not an XSS vector: jsonLdScript()
         JSON-encodes the value and escapes "<", so no content can close the tag. -->
    <!-- eslint-disable-next-line svelte/no-at-html-tags, no-useless-escape -->
    {@html `<script type="application/ld+json">${jsonLd}<\/script>`}
  {/if}
</svelte:head>
